import type { ReactNode } from 'react'

import {
  ResponsiveDialogBody,
  ResponsiveDialogDescription,
  ResponsiveDialogFooter,
  ResponsiveDialogHero,
  ResponsiveDialogTitle,
} from '@/components/common/responsive-dialog'

import { BenefitsList } from '../benefits-list'
import { ContactAdminCard } from '../contact-admin-card'

interface UpsellDialogShellProps {
  icon: ReactNode
  title: string
  planLine?: ReactNode
  description: string
  benefits?: { title: string; items: Array<string> }
  contactAdmin?: { title: string; description: string }
  /** Extra body content, below the benefits and contact blocks. */
  content?: ReactNode
  /** CTAs and exits; they stack in the footer. */
  actions?: ReactNode
}

/**
 * Shared hero layout for the upsell dialog family (feature paywall, contextual
 * upsell, plan paywall): admin variants pass benefits + actions, user variants
 * pass benefits + contactAdmin, managed variants pass contactAdmin only.
 */
export function UpsellDialogShell({
  icon,
  title,
  planLine,
  description,
  benefits,
  contactAdmin,
  content,
  actions,
}: UpsellDialogShellProps) {
  const showBenefits = benefits !== undefined && benefits.items.length > 0
  const hasContent =
    showBenefits || contactAdmin !== undefined || content !== undefined

  return (
    <>
      <ResponsiveDialogBody layout="centered">
        <ResponsiveDialogHero icon={icon}>
          <ResponsiveDialogTitle className="text-2xl">
            {title}
          </ResponsiveDialogTitle>
          {planLine != null && (
            <p className="text-muted-foreground text-sm">{planLine}</p>
          )}
          <ResponsiveDialogDescription>
            {description}
          </ResponsiveDialogDescription>
        </ResponsiveDialogHero>

        {hasContent && (
          <div className="mt-6 space-y-6">
            {showBenefits && (
              <BenefitsList title={benefits.title} benefits={benefits.items} />
            )}
            {contactAdmin && (
              <ContactAdminCard
                title={contactAdmin.title}
                description={contactAdmin.description}
              />
            )}
            {content}
          </div>
        )}
      </ResponsiveDialogBody>

      {actions && (
        <ResponsiveDialogFooter layout="stacked">
          {actions}
        </ResponsiveDialogFooter>
      )}
    </>
  )
}
